/**
 * BB-3509: React 19 removed `defaultProps` resolution for function/forwardRef
 * components from the automatic JSX runtime (`createElement` still applies
 * it). The archived FluentUI Northstar 0.66 dists rely on it at runtime in
 * 239 components, and first-party JSX that renders them goes through this
 * runtime. This restores the React 18 merge semantics: undefined-only fill,
 * props-then-defaults key order, never mutating the caller's object.
 *
 * Unlike React 18's jsx runtime (which read `type.defaultProps` on the outer
 * type and left memo() to the reconciler's resolveDefaultProps), this also
 * unwraps memo() wrappers so `memo(ComponentWithDefaults)` keeps its
 * defaults. lazy() cannot be unwrapped synchronously (the component isn't
 * loaded at element-creation time) — a lazy component relying on defaultProps
 * is not supported; none exist in the FluentUI dists or first-party code
 * (first-party defaultProps is lint-banned).
 *
 * First-party code must not depend on this: `.defaultProps =` assignments are
 * banned by `scripts/lint/no-default-props.mjs` (part of `pnpm lint`), so the
 * shim's dependency set can only shrink. Delete the package (and the
 * `jsxImportSource` entries pointing at it) when FluentUI retires to rocco.
 */
const REACT_MEMO_TYPE = Symbol.for('react.memo');

function resolveDefaults(type) {
    let t = type;
    while (t !== null && typeof t === 'object' && t.$$typeof === REACT_MEMO_TYPE) {
        t = t.type;
    }
    if (t == null) return null;
    const defaults = t.defaultProps;
    return defaults == null ? null : defaults;
}

export function withDefaultProps(type, props) {
    // Host elements ('div') and components without defaults: fast no-op.
    if (typeof type === 'string') return props;
    const defaults = resolveDefaults(type);
    if (defaults === null) return props;
    const merged = { ...props };
    for (const key in defaults) {
        if (merged[key] === undefined) merged[key] = defaults[key];
    }
    return merged;
}
